import { useState, useRef, useCallback, useEffect } from "react";
import { Link, router } from "@inertiajs/react";
import { Badge, Text, Heading, Callout, Flex, Button, Card, Box, Progress } from "@radix-ui/themes";
import { ChevronLeft, ChevronRight, ArrowRight, Users, X, Check, Car, Camera } from "lucide-react";
import UserPageShell from "@/shared/users/UserPageShell";
import { badgeColors } from "@/shared/constants/constants";
import type { CarCardCar } from "@/types/shared";
import type {
  DashboardsShowApplication,
  DashboardsShowMediaGroup,
  DashboardsShowProps,
  DashboardsShowQuickApply,
  DashboardsShowSelfReportEvent,
  DashboardsShowSetupStep,
  DashboardsShowStartStep,
  DashboardsShowSuggestedEvent,
} from "@/types/dashboards_show";

// ── Helpers ──────────────────────────────────────────────────────────────────

const STEPS = ["Submitted", "Review", "Confirmed"];

// Status → which step number is "active" (1-indexed)
const STATUS_ACTIVE_STEP: Record<string, number> = {
  draft:     1,
  pending:   2,
  approved:  4, // past step 3 = all done
  rejected:  2,
  withdrawn: 1,
  waitlisted: 2,
};

const STATUS_LABELS: Record<string, string> = {
  draft:     "Draft",
  pending:   "Under review",
  approved:  "Accepted",
  rejected:  "Not accepted",
  withdrawn: "Withdrawn",
  waitlisted: "Waitlisted",
};

// ── Sub-components ───────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p style={{
      fontSize: "10.5px",
      fontWeight: 500,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: "var(--gray-9)",
      marginBottom: "0.75rem",
    }}>
      {children}
    </p>
  );
}

// Statuses that end the application before it's confirmed. A tracker would
// show them stalled mid-way, so they get their badge instead.
const ENDED_STATUSES = ["rejected", "withdrawn"];

/**
 * Submitted — Under review — Confirmed as connected chips: done ones filled,
 * the current one outlined, the rest grey. The middle chip says "Waitlisted"
 * when that's where they are.
 */
function StatusSteps({ status }: { status: string }) {
  if (ENDED_STATUSES.includes(status)) {
    return (
      <Box>
        <Badge size="2" variant="soft" color={badgeColors[status]} radius="full">
          {STATUS_LABELS[status] ?? status}
        </Badge>
      </Box>
    );
  }

  const activeStep = STATUS_ACTIVE_STEP[status] ?? 1;

  return (
    <Flex align="center" gap="2" aria-label={`Status: ${STATUS_LABELS[status] ?? status}`}>
      {STEPS.map((step, i) => {
        const stepNum = i + 1;
        const isDone = stepNum < activeStep;
        const isActive = stepNum === activeStep;
        const label = isActive && stepNum === 2 ? (STATUS_LABELS[status] ?? step) : step;

        return (
          <div key={step} className="contents">
            <span
              className="inline-flex shrink-0 items-center whitespace-nowrap rounded-full px-2.5 py-1 text-xs"
              style={{
                background: isDone ? "var(--accent-3)" : isActive ? "var(--color-panel-solid)" : "var(--gray-3)",
                color: isDone || isActive ? "var(--accent-11)" : "var(--gray-11)",
                boxShadow: isActive ? "inset 0 0 0 1.5px var(--accent-9)" : undefined,
                fontWeight: isActive ? 600 : 500,
              }}
              aria-current={isActive ? "step" : undefined}
            >
              {isDone && <Check size={12} aria-hidden style={{ marginRight: 4 }} />}
              {label}
            </span>
            {i < STEPS.length - 1 && (
              <span
                className="h-0.5 min-w-3 flex-1 rounded"
                style={{ background: isDone ? "var(--accent-9)" : "var(--gray-5)" }}
                aria-hidden
              />
            )}
          </div>
        );
      })}
    </Flex>
  );
}

function AppNote({ status, event_date }: { status: string; event_date: string }) {
  const notes: Record<string, React.ReactNode> = {
    pending:   <>Organizers typically respond within <strong>3–5 business days.</strong> Event date is <strong>{event_date}</strong>.</>,
    draft:     <>You have a <strong>saved draft.</strong> Submit before the event on <strong>{event_date}</strong>.</>,
    approved:  <>You're in. <strong>{event_date}.</strong> Check your email.</>,
    rejected:  <>This application was not accepted for <strong>{event_date}</strong>.</>,
    withdrawn: <>You withdrew from this event. The event date was <strong>{event_date}</strong>.</>,
    waitlisted: <>The media spots are full, so you're <strong>on the waitlist.</strong> If one opens up you're in automatically. Event date is <strong>{event_date}</strong>.</>,
  };
  return (
    <div style={{
      fontSize: "12.5px",
      color: "var(--gray-11)",
      background: "var(--gray-2)",
      borderRadius: 8,
      padding: "10px 14px",
      lineHeight: 1.5,
    }}>
      {notes[status] ?? <span>Event date: {event_date}</span>}
    </div>
  );
}

/** The event's poster, or its date as a tile when there isn't one. */
function EventImage({ app }: { app: DashboardsShowApplication }) {
  if (app.poster_url) {
    return (
      <img
        src={app.poster_url}
        alt={`${app.event_name} poster`}
        style={{ display: "block", width: "100%", height: "100%", minHeight: 180, objectFit: "cover", borderRadius: 10 }}
      />
    );
  }

  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      gap="1"
      style={{ height: "100%", minHeight: 180, borderRadius: 10, background: "var(--gray-3)" }}
      aria-hidden
    >
      <Text size="3" weight="bold" style={{ color: "var(--accent-11)", textTransform: "uppercase", letterSpacing: "0.08em" }}>
        {app.event_month}
      </Text>
      <Text style={{ fontSize: 56, lineHeight: 1, fontWeight: 700, color: "var(--gray-12)" }}>{app.event_day}</Text>
      <Text size="2" color="gray">{app.event_location}</Text>
    </Flex>
  );
}

function ApplicationCarousel({
  applications,
  totalCount,
}: {
  applications: DashboardsShowApplication[];
  totalCount: number;
}) {
  const [current, setCurrent] = useState(0);
  const touchStartX = useRef(0);
  const total = applications.length;

  const goTo = useCallback((idx: number) => {
    setCurrent(Math.max(0, Math.min(idx, total - 1)));
  }, [total]);

  if (total === 0) {
    return (
      <div style={{
        background: "var(--color-panel-solid)",
        border: "0.5px dashed var(--gray-7)",
        borderRadius: 16,
        padding: "1.5rem 1.25rem",
        textAlign: "center",
      }}>
        <div style={{ fontSize: 28, marginBottom: "0.75rem" }}>🏁</div>
        <Text as="p" size="3" weight="medium" mb="1">No applications yet</Text>
        <Text as="p" size="2" color="gray" mb="4">
          Browse open events and submit your first application.
        </Text>
        <Link href="/events">
          <button style={{
            fontSize: 13, fontWeight: 500, padding: "9px 20px", borderRadius: 8,
            background: "var(--gray-12)", color: "var(--gray-1)", border: "none", cursor: "pointer",
          }}>
            Find events →
          </button>
        </Link>
      </div>
    );
  }

  const app = applications[current];

  return (
    <div>
      {/* Slide card */}
      <div
        style={{ overflow: "hidden", borderRadius: 16, minWidth: 0 }}
        onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => {
          const diff = touchStartX.current - e.changedTouches[0].clientX;
          if (Math.abs(diff) > 40) goTo(diff > 0 ? current + 1 : current - 1);
        }}
      >
        <Card size="3" style={{ overflowWrap: "break-word" }}>
          <Flex gap="5" wrap="wrap">
            <Box style={{ flex: "1 1 220px" }}>
              <EventImage app={app} />
            </Box>

            <Flex direction="column" gap="3" style={{ flex: "1 1 300px", minWidth: 0 }}>
              <Flex direction="column" gap="1">
                <Text
                  size="1"
                  weight="bold"
                  style={{ color: "var(--accent-11)", textTransform: "uppercase", letterSpacing: "0.06em" }}
                >
                  Application · as {app.participant.name}
                </Text>
                <Link href={`/events/${app.event_id}`} style={{ textDecoration: "none" }}>
                  <Text as="p" size="5" weight="bold" style={{ color: "var(--gray-12)" }}>
                    {app.event_name}
                  </Text>
                </Link>
                <Text as="p" size="2" color="gray">
                  {app.event_location} · {app.event_date}
                </Text>
              </Flex>

              <StatusSteps status={app.status} />
              <AppNote status={app.status} event_date={app.event_date} />

              {/* "View application" is the event page until applications get a
                  page of their own (a scannable pass, say). */}
              <Flex gap="3" wrap="wrap" mt="auto">
                <Button asChild size="3">
                  <Link href={`/events/${app.event_id}`}>View application</Link>
                </Button>
                <Button asChild size="3" variant="outline">
                  <Link href="/applications">All applications ({totalCount})</Link>
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </Card>
      </div>

      {/* Controls — only shown when there's more than one card */}
      {total > 1 && (
        <div style={{
          display: "flex", alignItems: "center",
          justifyContent: "space-between", marginTop: "0.875rem",
        }}>
          {/* Dot indicators */}
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {applications.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                aria-label={`Go to slide ${i + 1}`}
                style={{
                  border: "none", padding: 0, cursor: "pointer",
                  height: 6, borderRadius: i === current ? 3 : "50%",
                  width: i === current ? 18 : 6,
                  background: i === current ? "var(--gray-12)" : "var(--gray-7)",
                  transition: "all 0.2s",
                }}
              />
            ))}
          </div>

          {/* Counter + prev/next */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 11, fontFamily: "monospace", color: "var(--gray-9)" }}>
              {current + 1} / {total}
            </span>
            <div style={{ display: "flex", gap: 6 }}>
              <button
                onClick={() => goTo(current - 1)}
                disabled={current === 0}
                aria-label="Previous"
                style={{
                  width: 30, height: 30, borderRadius: "50%",
                  border: "0.5px solid var(--gray-7)", background: "var(--color-panel-solid)",
                  cursor: current === 0 ? "default" : "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  opacity: current === 0 ? 0.3 : 1, color: "var(--gray-11)",
                }}
              >
                <ChevronLeft size={14} />
              </button>
              <button
                onClick={() => goTo(current + 1)}
                disabled={current === total - 1}
                aria-label="Next"
                style={{
                  width: 30, height: 30, borderRadius: "50%",
                  border: "0.5px solid var(--gray-7)", background: "var(--color-panel-solid)",
                  cursor: current === total - 1 ? "default" : "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  opacity: current === total - 1 ? 0.3 : 1, color: "var(--gray-11)",
                }}
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Events you said you'd be at ──────────────────────────────────────────────

/**
 * Upcoming events the user said they're going to, and past ones where they
 * never said whether they made it. Each report is a car or a media group;
 * the buttons hit the same endpoints as the event page and come back here.
 */
function SelfReportEvents({
  upcoming,
  unconfirmed,
}: {
  upcoming: DashboardsShowSelfReportEvent[];
  unconfirmed: DashboardsShowSelfReportEvent[];
}) {
  const base = (eventId: number) => `/events/${eventId}/self_reports`;
  const confirm = (eventId: number, reportId: number) =>
    router.patch(`${base(eventId)}/${reportId}/confirm`, {}, { preserveScroll: true });
  const remove = (eventId: number, reportId: number) =>
    router.delete(`${base(eventId)}/${reportId}`, { preserveScroll: true });

  return (
    <Flex direction="column" gap="5">
      {unconfirmed.length > 0 && (
        <Box>
          <SectionLabel>Did you make it?</SectionLabel>
          <Flex direction="column" gap="3">
            {unconfirmed.map((event) => (
              <Card key={event.id}>
                <SelfReportEventHeading event={event} />
                <Flex direction="column" gap="2" mt="3">
                  {event.reports.map((report) => (
                    <Flex key={report.id} align="center" justify="between" gap="3" wrap="wrap">
                      <Text size="2">{report.label}</Text>
                      <Flex gap="2">
                        <Button size="1" onClick={() => confirm(event.id, report.id)}>
                          I was there
                        </Button>
                        <Button size="1" variant="soft" color="gray" onClick={() => remove(event.id, report.id)}>
                          I didn't go
                        </Button>
                      </Flex>
                    </Flex>
                  ))}
                </Flex>
              </Card>
            ))}
          </Flex>
        </Box>
      )}

      {upcoming.length > 0 && (
        <Box>
          <SectionLabel>Upcoming</SectionLabel>
          <Flex direction="column" gap="3">
            {upcoming.map((event) => (
              <Card key={event.id}>
                <SelfReportEventHeading event={event} />
                <Flex direction="column" gap="1" mt="3">
                  {event.reports.map((report) => (
                    <Flex key={report.id} align="center" justify="between" gap="3">
                      <Text size="2" color="gray">
                        Going with {report.label}
                      </Text>
                      <Button
                        size="1"
                        variant="ghost"
                        color="gray"
                        aria-label={`Not going with ${report.label}`}
                        onClick={() => remove(event.id, report.id)}
                      >
                        <X size={14} />
                      </Button>
                    </Flex>
                  ))}
                </Flex>
              </Card>
            ))}
          </Flex>
        </Box>
      )}
    </Flex>
  );
}

function SelfReportEventHeading({ event }: { event: DashboardsShowSelfReportEvent }) {
  return (
    <Box>
      <Link href={`/events/${event.id}`} style={{ textDecoration: "none" }}>
        <Text as="p" size="3" weight="medium" style={{ color: "var(--gray-12)" }}>
          {event.name}
        </Text>
      </Link>
      <Text as="p" size="2" color="gray">
        {event.track_name} · {event.date}
      </Text>
    </Box>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

// ── Home heading ─────────────────────────────────────────────────────────────

const INTENT_LABELS: Record<DashboardsShowProps["intents"][number], string> = {
  media: "Photographer / drone pilot",
  driver: "Driver",
  find: "Finding tracks and events",
};

function HomeHeading({ intents, brandNew }: { intents: DashboardsShowProps["intents"]; brandNew: boolean }) {
  return (
    <Flex direction="column" gap="2">
      <Heading size="7">{brandNew ? "Welcome to Driftables" : "Home"}</Heading>
      <Flex align="center" gap="2" wrap="wrap">
        {intents.length > 0 ? (
          <>
            <Text size="2" color="gray">You're here as:</Text>
            {intents.map((intent) => (
              <Badge key={intent} variant="soft" radius="full">
                {INTENT_LABELS[intent]}
              </Badge>
            ))}
            <Link href="/welcome?step=intents" style={{ fontSize: 13, color: "var(--accent-11)" }}>
              Change
            </Link>
          </>
        ) : (
          <Link href="/welcome?step=intents" style={{ fontSize: 14, color: "var(--accent-11)" }}>
            Tell us what brings you here, and we'll set this page up around it →
          </Link>
        )}
      </Flex>
    </Flex>
  );
}

// ── Start here (brand-new users) ─────────────────────────────────────────────

function StartHere({ steps }: { steps: DashboardsShowStartStep[] }) {
  // The first step not yet done is the one to do next.
  const nextIndex = steps.findIndex((s) => !s.done);

  return (
    <section>
      <Heading size="5" mb="4">Start here</Heading>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {steps.map((step, i) => {
          const isNext = i === nextIndex;
          return (
            <Card
              key={step.title}
              size="2"
              style={isNext ? { boxShadow: "inset 0 0 0 2px var(--accent-9)" } : undefined}
            >
              <Flex direction="column" gap="2" height="100%">
                <Flex
                  align="center"
                  justify="center"
                  style={{
                    width: 32, height: 32, borderRadius: "50%", fontWeight: 600, fontSize: 14,
                    background: step.done || isNext ? "var(--accent-9)" : "var(--gray-3)",
                    color: step.done || isNext ? "var(--accent-contrast)" : "var(--gray-11)",
                  }}
                >
                  {step.done ? <Check size={16} aria-label="Done" /> : i + 1}
                </Flex>
                <Text size="4" weight="bold">{step.title}</Text>
                <Text size="2" color="gray" style={{ flex: 1 }}>{step.body}</Text>
                {!step.done && (
                  <Button asChild size="3" variant={isNext ? "solid" : "outline"}>
                    <Link href={step.href}>{step.cta}</Link>
                  </Button>
                )}
              </Flex>
            </Card>
          );
        })}
      </div>
    </section>
  );
}

// ── Quick apply (returning media) ────────────────────────────────────────────

function QuickApplyCard({ card }: { card: DashboardsShowQuickApply }) {
  const [submitting, setSubmitting] = useState(false);
  const renew = card.state === "renew";

  const apply = () => {
    setSubmitting(true);
    router.post(card.apply_path, {}, { preserveScroll: true, onFinish: () => setSubmitting(false) });
  };

  return (
    <Card size="3" style={{ boxShadow: `inset 0 0 0 2px var(--${renew ? "amber" : "accent"}-9)` }}>
      <Flex gap="5" align="center" wrap="wrap">
        <Flex direction="column" gap="1" style={{ flex: "1 1 260px", minWidth: 0 }}>
          <Text
            size="1"
            weight="bold"
            style={{ color: `var(--${renew ? "amber" : "accent"}-11)`, textTransform: "uppercase", letterSpacing: "0.06em" }}
          >
            {renew
              ? `Next at ${card.track_name} · credential needed`
              : `Next at ${card.track_name}`}
          </Text>
          <Text size="5" weight="bold">{card.event_name}</Text>
          <Text size="2" color="gray">
            {[card.club_name && `Hosted by ${card.club_name}`, card.date, card.closes_on && `closes ${card.closes_on}`]
              .filter(Boolean)
              .join(" · ")}
          </Text>
          <Text size="2" color="gray">
            {renew
              ? `${card.track_name} needs a current media credential before you can apply.`
              : `Applies as ${card.participant_name} with what's already on file.`}
          </Text>
        </Flex>
        <Flex direction="column" gap="2" align="stretch">
          {renew && card.renew_path ? (
            <Button asChild size="3" color="amber">
              <Link href={card.renew_path}>Renew credential</Link>
            </Button>
          ) : (
            <>
              <Button size="3" onClick={apply} disabled={submitting}>
                {submitting ? "Applying…" : "Apply in one click"}
              </Button>
              <Button asChild size="2" variant="ghost">
                <Link href={card.review_path}>Review first</Link>
              </Button>
            </>
          )}
        </Flex>
      </Flex>
    </Card>
  );
}

// ── Suggested events ─────────────────────────────────────────────────────────

function eventBadge(event: DashboardsShowSuggestedEvent): string | null {
  if (!event.applications_open) return null;
  if (event.accepts_media && !event.accepts_drivers) return "Media apps open";
  if (event.accepts_drivers && !event.accepts_media) return "Driver apps open";
  return "Applications open";
}

function SuggestedEvents({
  events,
  homeArea,
}: {
  events: DashboardsShowSuggestedEvent[];
  homeArea: string | null;
}) {
  return (
    <section>
      <Flex justify="between" align="baseline" mb="4" gap="3" wrap="wrap">
        <Heading size="5">{homeArea ? `Coming up near ${homeArea}` : "Coming up for you"}</Heading>
        <Link href="/events" style={{ fontSize: 13, color: "var(--accent-11)" }}>Browse all events →</Link>
      </Flex>

      {events.length === 0 ? (
        <Card size="2" variant="surface">
          <Flex direction="column" gap="3" align="start">
            <Text size="2" color="gray">
              Follow tracks or tell us your area, and their upcoming events show up here.
            </Text>
            <Flex gap="2" wrap="wrap">
              <Button asChild variant="soft"><Link href="/tracks">Find tracks</Link></Button>
              {!homeArea && <Button asChild variant="outline"><Link href="/welcome">Set your area</Link></Button>}
            </Flex>
          </Flex>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {events.map((event) => {
            const badge = eventBadge(event);
            return (
              <Link key={event.id} href={`/events/${event.id}`} style={{ textDecoration: "none" }}>
                <Card size="2" style={{ height: "100%" }}>
                  <Flex direction="column" gap="1" height="100%">
                    <Text size="1" color="gray">
                      {event.short_date}
                      {event.reason === "nearby" && event.distance_miles != null
                        ? ` · ${event.distance_miles} mi away`
                        : " · You follow this"}
                    </Text>
                    <Text size="3" weight="bold" style={{ color: "var(--gray-12)" }}>{event.name}</Text>
                    <Text size="2" color="gray" style={{ flex: 1 }}>
                      {event.track_name}
                      {event.club_name ? ` · ${event.club_name}` : ""}
                    </Text>
                    {badge && (
                      <Box mt="2">
                        <Badge variant="soft" radius="full">{badge}</Badge>
                      </Box>
                    )}
                  </Flex>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ── Side rail ────────────────────────────────────────────────────────────────

function SetupChecklist({ steps }: { steps: DashboardsShowSetupStep[] }) {
  const done = steps.filter((s) => s.done).length;
  if (done === steps.length) return null;

  return (
    <Card size="2">
      <Flex direction="column" gap="3">
        <Text size="3" weight="bold">Finish setting up</Text>
        <Flex align="center" gap="3">
          <Box style={{ flex: 1 }}>
            <Progress value={(done / steps.length) * 100} aria-label="Setup progress" />
          </Box>
          <Text size="1" color="gray">{done} of {steps.length}</Text>
        </Flex>
        <Flex direction="column" gap="2">
          {steps.map((step) =>
            step.done ? (
              <Flex
                key={step.key}
                align="center"
                gap="2"
                style={{ padding: "10px 12px", border: "1px solid var(--gray-5)", borderRadius: 8 }}
              >
                <Check size={16} color="var(--green-11)" aria-label="Done" />
                <Text size="2" color="gray" style={{ textDecoration: "line-through" }}>{step.label}</Text>
              </Flex>
            ) : (
              <Link
                key={step.key}
                href={step.href}
                className="flex min-h-11 items-center gap-2 rounded-lg border border-gray7 px-3 text-gray12 no-underline hover:bg-gray3"
              >
                <span className="h-4 w-4 shrink-0 rounded-full border-2 border-gray8" aria-hidden />
                <Text size="2">{step.label}</Text>
                <ArrowRight size={14} style={{ marginLeft: "auto" }} aria-hidden />
              </Link>
            ),
          )}
        </Flex>
      </Flex>
    </Card>
  );
}

const CAR_STATUS: Record<string, { label: string; color: "green" | "amber" | "red" }> = {
  approved: { label: "Approved", color: "green" },
  pending: { label: "In review", color: "amber" },
  rejected: { label: "Not approved", color: "red" },
};

function GarageCard({ cars }: { cars: CarCardCar[] }) {
  return (
    <Card size="2">
      <Flex direction="column" gap="3">
        <Flex justify="between" align="center">
          <Text size="3" weight="bold">Your garage</Text>
          <Link href="/driver_cars/new" style={{ fontSize: 13, color: "var(--accent-11)" }}>+ Add car</Link>
        </Flex>
        {cars.length === 0 ? (
          <Text size="2" color="gray">No cars yet. Add one to apply to drive and log the events you ran.</Text>
        ) : (
          cars.map((car) => {
            const status = car.review_status ? CAR_STATUS[car.review_status] : null;
            return (
              <Link key={car.id} href={`/driver_cars/${car.id}`} style={{ textDecoration: "none" }}>
                <Flex align="center" gap="3">
                  {car.cover_thumb_url ? (
                    <img
                      src={car.cover_thumb_url}
                      alt=""
                      style={{ width: 56, height: 40, objectFit: "cover", borderRadius: 6, flexShrink: 0 }}
                    />
                  ) : (
                    <Flex
                      align="center"
                      justify="center"
                      style={{ width: 56, height: 40, borderRadius: 6, background: "var(--gray-3)", flexShrink: 0 }}
                    >
                      <Car size={18} color="var(--gray-9)" aria-hidden />
                    </Flex>
                  )}
                  <Text size="2" weight="medium" style={{ flex: 1, color: "var(--gray-12)" }}>
                    {[car.year, car.make, car.model].filter(Boolean).join(" ")}
                  </Text>
                  {status && <Badge color={status.color} radius="full">{status.label}</Badge>}
                </Flex>
              </Link>
            );
          })
        )}
      </Flex>
    </Card>
  );
}

function MediaGroupCard({ group }: { group: DashboardsShowMediaGroup | null }) {
  return (
    <Card size="2">
      <Flex direction="column" gap="3">
        <Text size="3" weight="bold">Your media group</Text>
        {group ? (
          <>
            <Flex align="center" gap="3">
              {group.logo_thumb_url ? (
                <img
                  src={group.logo_thumb_url}
                  alt=""
                  style={{ width: 40, height: 40, objectFit: "cover", borderRadius: "50%" }}
                />
              ) : (
                <Flex
                  align="center"
                  justify="center"
                  style={{ width: 40, height: 40, borderRadius: "50%", background: "var(--gray-3)" }}
                >
                  <Camera size={18} color="var(--gray-9)" aria-hidden />
                </Flex>
              )}
              <Box>
                <Text as="p" size="2" weight="medium">{group.name}</Text>
                <Text as="p" size="1" color="gray">{group.listing_label}</Text>
              </Box>
            </Flex>
            <Flex gap="4">
              <Link href={group.edit_path} style={{ fontSize: 13, color: "var(--accent-11)" }}>Edit</Link>
              <Link href={group.public_path} style={{ fontSize: 13, color: "var(--accent-11)" }}>Public page</Link>
            </Flex>
          </>
        ) : (
          <>
            <Text size="2" color="gray">Organizers see your group when you apply to shoot an event.</Text>
            <Button asChild variant="soft"><Link href="/media_group_participant/new">Set up media group</Link></Button>
          </>
        )}
      </Flex>
    </Card>
  );
}

// Dismissals are a per-browser convenience, so localStorage — which can throw
// in a private window, hence the try/catch.
const DISMISSED_KEY = "driftables:dismissed-home-nudges";

interface Nudge {
  key: string;
  eyebrow: string;
  title: string;
  body: string;
  href: string;
  cta: string;
}

function Nudges({ nudges }: { nudges: Nudge[] }) {
  const [dismissed, setDismissed] = useState<string[]>([]);

  useEffect(() => {
    try {
      setDismissed(JSON.parse(localStorage.getItem(DISMISSED_KEY) ?? "[]"));
    } catch {
      // Nothing dismissed, then.
    }
  }, []);

  const dismiss = (key: string) => {
    const next = [...dismissed, key];
    setDismissed(next);
    try {
      localStorage.setItem(DISMISSED_KEY, JSON.stringify(next));
    } catch {
      // Gone for this page view only.
    }
  };

  return (
    <>
      {nudges
        .filter((n) => !dismissed.includes(n.key))
        .map((n) => (
          <Card key={n.key} size="2">
            <Flex direction="column" gap="2">
              <Text size="1" weight="bold" style={{ color: "var(--accent-11)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                {n.eyebrow}
              </Text>
              <Text size="3" weight="bold">{n.title}</Text>
              <Text size="2" color="gray">{n.body}</Text>
              <Flex gap="2" mt="1" wrap="wrap">
                <Button asChild variant="soft"><Link href={n.href}>{n.cta}</Link></Button>
                <Button variant="ghost" color="gray" onClick={() => dismiss(n.key)}>Dismiss</Button>
              </Flex>
            </Flex>
          </Card>
        ))}
    </>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function Dashboard({
  intents,
  home_area,
  brand_new,
  recent_applications,
  applications_count,
  quick_apply,
  suggested_events,
  pending_team_invites,
  upcoming_self_reports,
  unconfirmed_self_reports,
  setup_steps,
  start_steps,
  garage,
  media_group,
  is_host,
}: DashboardsShowProps) {
  // Someone who came to find tracks and events sees those first; everyone
  // else sees what they've got going on first.
  const eventsFirst = intents[0] === "find";
  const events = <SuggestedEvents events={suggested_events} homeArea={home_area} />;

  // Gentle offers for the things they didn't say they're here for.
  const nudges: Nudge[] = [];
  if (!intents.includes("driver") && garage.length === 0) {
    nudges.push({
      key: "driver", eyebrow: "Also driving?", title: "Add your car",
      body: "Log the events you've run and show up on event pages.", href: "/driver_cars/new", cta: "Add a car",
    });
  }
  if (!intents.includes("media") && !media_group) {
    nudges.push({
      key: "media", eyebrow: "Shoot photo or video?", title: "Set up a media group",
      body: "Apply to media days and get credentialed at tracks.", href: "/media_group_participant/new", cta: "Set up",
    });
  }
  if (!is_host) {
    nudges.push({
      key: "host", eyebrow: "Running events?", title: "Become a verified host",
      body: "Track and club staff can manage events, applications and credentials.",
      href: "/track_manager/verification", cta: "Learn how",
    });
  }

  return (
    <UserPageShell title="Home" wide>
      {/* ── Pending team invites ── */}
      {pending_team_invites.length > 0 && (
        <section>
          <Flex direction="column" gap="2">
            {pending_team_invites.map((invite) => (
              <Callout.Root key={invite.id} variant="surface">
                <Callout.Icon><Users size={16} /></Callout.Icon>
                <Callout.Text>
                  <Flex
                    justify="between"
                    align={{ initial: "start", sm: "center" }}
                    direction={{ initial: "column", sm: "row" }}
                    gap={{ initial: "2", sm: "4" }}
                  >
                    <Text size="2">
                      You've been invited to join <strong>{invite.team_name}</strong>
                      {invite.location ? ` · ${invite.location}` : ""}
                    </Text>
                    <Link href={`/team_invitations/${invite.id}`}>
                      <Button size="1" variant="soft" style={{ whiteSpace: "nowrap" }}>
                        Review invite →
                      </Button>
                    </Link>
                  </Flex>
                </Callout.Text>
              </Callout.Root>
            ))}
          </Flex>
        </section>
      )}

      <HomeHeading intents={intents} brandNew={brand_new} />

      {brand_new && start_steps.length > 0 && <StartHere steps={start_steps} />}

      {/* Main column plus a rail; the rail drops below on narrow screens. */}
      <div className="flex flex-wrap items-start gap-10">
        <div className="flex min-w-0 flex-[999_1_520px] flex-col gap-10">
          {/* ── Quick apply ── */}
          {quick_apply.length > 0 && (
            <Flex direction="column" gap="3">
              {quick_apply.map((card) => (
                <QuickApplyCard key={card.event_id} card={card} />
              ))}
            </Flex>
          )}

          {eventsFirst && events}

          {/* ── Up next ── */}
          {recent_applications.length > 0 && (
            <section>
              <Heading size="5" mb="4">Up next</Heading>
              <ApplicationCarousel applications={recent_applications} totalCount={applications_count} />
            </section>
          )}

          {/* ── Events you said you'd be at ── */}
          {(upcoming_self_reports.length > 0 || unconfirmed_self_reports.length > 0) && (
            <section>
              <Heading size="5" mb="4">Events you said you'd be at</Heading>
              <SelfReportEvents upcoming={upcoming_self_reports} unconfirmed={unconfirmed_self_reports} />
            </section>
          )}

          {!eventsFirst && events}
        </div>

        <aside aria-label="Your setup" className="flex min-w-[280px] flex-[1_1_300px] flex-col gap-5 lg:max-w-[340px]">
          <SetupChecklist steps={setup_steps} />
          {(intents.includes("driver") || garage.length > 0) && <GarageCard cars={garage} />}
          {(intents.includes("media") || media_group) && <MediaGroupCard group={media_group} />}
          <Nudges nudges={nudges} />
        </aside>
      </div>
    </UserPageShell>
  );
}
